/**
 * Clients Page - Updated with full-page client details
 */

import React, { useState, useEffect } from 'react';
import { ClientList } from '@components/clients/client_list';
import { ClientForm } from '@components/clients/client_form';
import { ClientDetails } from '@components/clients/client_details';
import { PaymentForm } from '@components/payments/payment_form';
import { PageLoading } from '@components/common/loading_spinner';
import { toast } from 'react-toastify';
import {
  Client,
  CreateClientPayload,
  Property,
  ClientActivity,
  ClientFinancialSummary,
  ClientConsumptionSummary
} from '@/types/client_types';
import { Invoice } from '@/types/invoice_types';
import { CreatePaymentPayload } from '@/types/payment_types';
import {
  fetch_clients,
  create_client,
  update_client,
  delete_client,
  fetch_client_properties,
  fetch_client_activities,
  fetch_client_financial_summary,
  fetch_client_consumption_summary
} from '@services/client_service';
import { fetch_invoices } from '@services/invoice_service';
import { create_payment } from '@services/payment_service';

type PageView = 'list' | 'details';

export const ClientsPage: React.FC = () => {
  // State management
  const [clients, set_clients] = useState<Client[]>([]);
  const [invoices, set_invoices] = useState<Invoice[]>([]);
  const [is_loading, set_is_loading] = useState(true);
  const [is_form_open, set_is_form_open] = useState(false);
  const [is_payment_form_open, set_is_payment_form_open] = useState(false);
  const [is_submitting, set_is_submitting] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [current_view, set_current_view] = useState<PageView>('list');

  const [selected_client, set_selected_client] = useState<Client | null>(null);
  const [editing_client, set_editing_client] = useState<Client | null>(null);
  const [form_mode, set_form_mode] = useState<'create' | 'edit'>('create');

  // Client details data
  const [client_properties, set_client_properties] = useState<Property[]>([]);
  const [client_activities, set_client_activities] = useState<ClientActivity[]>([]);
  const [financial_summary, set_financial_summary] = useState<ClientFinancialSummary | undefined>();
  const [consumption_summary, set_consumption_summary] = useState<ClientConsumptionSummary | undefined>();
  const [details_loading, set_details_loading] = useState(false);

  // Load clients on mount
  useEffect(() => {
    load_clients();
    load_invoices();
  }, []);

  const load_clients = async () => {
    try {
      set_is_loading(true);
      set_error(null);
      
      const data = await fetch_clients();

      console.log("clients", data)
      
      // Validate that data is an array
      if (Array.isArray(data)) {
        set_clients(data);
      } else {
        console.error('Invalid clients data format:', data);
        set_clients([]);
        set_error('Received invalid data format from server');
        toast.error('Failed to load clients: Invalid data format');
      }
    } catch (error: any) {
      console.error('Error loading clients:', error);
      set_clients([]);
      set_error(error?.message || 'Failed to load clients');
      toast.error(error?.message || 'Failed to load clients');
    } finally {
      set_is_loading(false);
    }
  };

  const load_invoices = async () => {
    try {
      const data = await fetch_invoices();
      set_invoices(data || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      // Don't show error toast for invoices, as they're supplementary
    }
  };

  const load_client_details = async (client: Client) => {
    try {
      set_details_loading(true);

      // Load all client details in parallel with error handling
      const [properties, activities, financial, consumption] = await Promise.allSettled([
        fetch_client_properties(client.id),
        fetch_client_activities(client.id),
        fetch_client_financial_summary(client.id),
        fetch_client_consumption_summary(client.id)
      ]);

      // Extract successful results
      set_client_properties(
        properties.status === 'fulfilled' && Array.isArray(properties.value) 
          ? properties.value 
          : []
      );
      set_client_activities(
        activities.status === 'fulfilled' && Array.isArray(activities.value)
          ? activities.value
          : []
      );
      set_financial_summary(
        financial.status === 'fulfilled' 
          ? financial.value 
          : undefined
      );
      set_consumption_summary(
        consumption.status === 'fulfilled'
          ? consumption.value
          : undefined
      );

      // Log any failures
      if (properties.status === 'rejected') {
        console.error('Failed to load properties:', properties.reason);
      }
      if (activities.status === 'rejected') {
        console.error('Failed to load activities:', activities.reason);
      }
      if (financial.status === 'rejected') {
        console.error('Failed to load financial summary:', financial.reason);
      }
      if (consumption.status === 'rejected') {
        console.error('Failed to load consumption summary:', consumption.reason);
      }

    } catch (error) {
      console.error('Error loading client details:', error);
      toast.error('Failed to load some client details');
    } finally {
      set_details_loading(false);
    }
  };

  const handle_add_client = () => {
    set_form_mode('create');
    set_editing_client(null);
    set_is_form_open(true);
  };

  const handle_edit_client = (client: Client) => {
    set_form_mode('edit');
    set_editing_client(client);
    set_is_form_open(true);
    set_current_view('list'); // Return to list view when editing
  };

  const handle_view_client = async (client: Client) => {
    set_selected_client(client);
    set_current_view('details');
    await load_client_details(client);
  };

  const handle_back_to_list = () => {
    set_current_view('list');
    set_selected_client(null);
  };

  const handle_record_payment = () => {
    if (!selected_client) return;
    
    // Check if client has outstanding invoices
    const client_invoices = invoices.filter(
      inv => inv.client?.toString() === selected_client.id?.toString() && 
             parseFloat((inv as any).amount_outstanding) > 0
    );
    
    if (client_invoices.length === 0) {
      toast.warning(`${selected_client.name} has no outstanding invoices.`);
      return;
    }
    
    set_is_payment_form_open(true);
  };

  const handle_payment_submit = async (data: CreatePaymentPayload) => {
    try {
      set_is_submitting(true);
      
      await create_payment(data);
      toast.success('Payment recorded successfully');
      
      set_is_payment_form_open(false);
      
      // Reload invoices and client details
      await load_invoices();
      if (selected_client) {
        await load_client_details(selected_client);
      }
    } catch (error: any) {
      console.error('Error recording payment:', error);
      toast.error(error?.message || 'Failed to record payment');
    } finally {
      set_is_submitting(false);
    }
  };

  const handle_delete_client = async (client: Client) => {
    if (!window.confirm(`Are you sure you want to delete ${client.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await delete_client(client.id);
      toast.success(`${client.name} deleted successfully`);
      await load_clients();
      set_current_view('list');
      set_selected_client(null);
    } catch (error: any) {
      console.error('Error deleting client:', error);
      
      // Display detailed error message
      if (error.errors) {
        // Show field-specific errors
        Object.entries(error.errors).forEach(([field, messages]) => {
          const fieldName = field === 'non_field_errors' 
            ? 'Error' 
            : field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          toast.error(`${fieldName}: ${(messages as string[]).join(', ')}`);
        });
      } else {
        toast.error(error?.message || 'Failed to delete client. They may have associated records.');
      }
    }
  };

  const handle_form_submit = async (data: CreateClientPayload) => {
    try {
      set_is_submitting(true);

      console.log('Submitting client data:', data);

      if (form_mode === 'create') {
        await create_client(data);
        toast.success('Client created successfully');
      } else if (editing_client) {
        await update_client(editing_client.id, data);
        toast.success('Client updated successfully');
      }

      set_is_form_open(false);
      await load_clients();
    } catch (error: any) {
      console.error('Error saving client:', error);
      
      // Handle ApiError with field-level validation errors
      if (error.errors && typeof error.errors === 'object') {
        // Display each field error separately
        Object.entries(error.errors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            const fieldName = field === 'non_field_errors' 
              ? 'Error' 
              : field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            
            messages.forEach(msg => {
              toast.error(`${fieldName}: ${msg}`, {
                autoClose: 5000,
              });
            });
          }
        });
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(`Failed to ${form_mode === 'create' ? 'create' : 'update'} client`);
      }
    } finally {
      set_is_submitting(false);
    }
  };

  const handle_export = async () => {
    try {
      if (!clients || clients.length === 0) {
        toast.warning('No clients to export');
        return;
      }

      toast.info('Exporting clients...');

      // Create CSV data
      const csv_data = clients.map(c => ({
        Name: c.name,
        Type: c.client_type,
        Email: c.email || '',
        Phone: c.phone || '',
        'ID Number': c.id_number || '',
        Address: c.address || '',
        Status: c.is_active ? 'Active' : 'Inactive',
        'Created Date': new Date(c.created_at).toLocaleDateString()
      }));

      // Convert to CSV
      const headers = Object.keys(csv_data[0]);
      const csv_content = [
        headers.join(','),
        ...csv_data.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(','))
      ].join('\n');

      // Download
      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Clients exported successfully');
    } catch (error) {
      console.error('Error exporting clients:', error);
      toast.error('Failed to export clients');
    }
  };

  if (is_loading) {
    return <PageLoading message="Loading clients..." />;
  }

  // Show details view
  if (current_view === 'details') {
    return (
      <>
        <ClientDetails
          client={selected_client}
          properties={client_properties}
          activities={client_activities}
          financial_summary={financial_summary}
          consumption_summary={consumption_summary}
          on_edit={() => selected_client && handle_edit_client(selected_client)}
          on_delete={() => selected_client && handle_delete_client(selected_client)}
          on_back={handle_back_to_list}
          on_record_payment={handle_record_payment}
          is_loading={details_loading}
        />
        
        {/* Payment Form Modal */}
        <PaymentForm
          is_open={is_payment_form_open}
          on_close={() => set_is_payment_form_open(false)}
          on_submit={handle_payment_submit}
          invoices={invoices}
          is_loading={is_submitting}
          pre_selected_client_id={selected_client?.id}
        />
      </>
    );
  }

  // Show error state if there's an error
  if (error && clients.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-charcoal">Clients</h1>
            <p className="text-medium-gray mt-1">
              Manage tenants and landlords in your water supply system
            </p>
          </div>
        </div>

        <div className="bg-error bg-opacity-10 border border-error rounded-lg p-6 text-center">
          <div className="text-error text-lg font-semibold mb-2">
            Failed to Load Clients
          </div>
          <p className="text-error mb-4">{error}</p>
          <button
            onClick={load_clients}
            className="px-4 py-2 bg-error text-white rounded-lg hover:bg-opacity-90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show list view
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-charcoal">Clients</h1>
          <p className="text-medium-gray mt-1">
            Manage tenants and landlords in your water supply system
          </p>
        </div>
      </div>

      {/* Client List */}
      <ClientList
        clients={clients}
        is_loading={is_loading}
        on_add_client={handle_add_client}
        on_view_client={handle_view_client}
        on_edit_client={handle_edit_client}
        on_delete_client={handle_delete_client}
        on_export={handle_export}
      />

      {/* Client Form Modal */}
      <ClientForm
        is_open={is_form_open}
        on_close={() => set_is_form_open(false)}
        on_submit={handle_form_submit}
        initial_data={editing_client || undefined}
        is_loading={is_submitting}
        mode={form_mode}
      />
    </div>
  );
};